import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
} from "@dnd-kit/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import { useFieldArray, useForm, useFormContext } from "react-hook-form";
import { DatePicker } from "@/shared/components/DatePicker";
import { Button } from "@/shared/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import {
  useCreatePatch,
  useDeletePatch,
  useUpdatePatch,
} from "../api/patch.mutations";
import {
  type PatchFormInput,
  type PatchFormValues,
  patchFormSchema,
} from "../settings.patch.schemas";
import {
  createEmptyPatch,
  findReferencedRemovals,
  type PatchResponse,
  patchToFormInput,
  type ReferencedRemoval,
} from "../settings.patch.utils";
import {
  PatchDeleteConfirmation,
  ReferencedRemovalConfirmation,
} from "./PatchEditorDialogs";
import { PatchRaidEditor } from "./PatchRaidEditor";
import {
  reorderByDragId,
  SortableItem,
  useSortableSensors,
} from "./SortableItem";

export function PatchEditor({
  patch,
  onClose,
  onSaved,
  onDeleted,
}: {
  patch?: PatchResponse;
  onClose: () => void;
  onSaved: (patch: PatchResponse) => void;
  onDeleted: () => void;
}) {
  const [baseline, setBaseline] = useState(patch);
  const [pendingConfirmation, setPendingConfirmation] = useState<{
    values: PatchFormValues;
    removals: ReferencedRemoval[];
  } | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [activeRaidId, setActiveRaidId] = useState<string | null>(null);
  const form = useForm<PatchFormInput, unknown, PatchFormValues>({
    resolver: zodResolver(patchFormSchema),
    defaultValues: patch ? patchToFormInput(patch) : createEmptyPatch(),
  });
  const raids = useFieldArray({
    control: form.control,
    name: "raids",
    keyName: "fieldKey",
  });
  const createPatch = useCreatePatch();
  const updatePatch = useUpdatePatch();
  const deletePatch = useDeletePatch();
  const sensors = useSortableSensors();
  const isSaving = createPatch.isPending || updatePatch.isPending;

  const save = (values: PatchFormValues) => {
    if (baseline) {
      updatePatch.mutate(
        { id: baseline.id, body: values },
        {
          onSuccess: (saved) => {
            setBaseline(saved);
            form.reset(patchToFormInput(saved));
            setPendingConfirmation(null);
            onSaved(saved);
          },
        },
      );
      return;
    }

    createPatch.mutate(values, {
      onSuccess: (saved) => {
        setBaseline(saved);
        form.reset(patchToFormInput(saved));
        onSaved(saved);
      },
    });
  };

  const onValidSubmit = (values: PatchFormValues) => {
    const removals = baseline ? findReferencedRemovals(baseline, values) : [];
    if (removals.length > 0) {
      setPendingConfirmation({ values, removals });
      return;
    }
    save(values);
  };

  const onRaidDragStart = ({ active }: DragStartEvent) =>
    setActiveRaidId(String(active.id));
  const onRaidDragEnd = (event: DragEndEvent) => {
    reorderByDragId(
      raids.fields,
      (raid) => raidDragId(raid.fieldKey),
      event,
      raids.move,
    );
    setActiveRaidId(null);
  };

  const activeRaidIndex = raids.fields.findIndex(
    (raid) => raidDragId(raid.fieldKey) === activeRaidId,
  );
  const activeRaid =
    activeRaidIndex >= 0 ? form.watch("raids")[activeRaidIndex] : null;

  const referenceCount = baseline ? Number(baseline.raidDayReferenceCount) : 0;

  return (
    <section className="w-full space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Tillbaka till patchlistan"
            onClick={onClose}
          >
            <ArrowLeft />
          </Button>
          <div>
            <h2 className="text-lg font-semibold">
              {baseline ? baseline.name : "Ny patch"}
            </h2>
          </div>
        </div>

        {baseline && (
          <div className="text-right">
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={referenceCount > 0}
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 />
              Ta bort patch
            </Button>
            {referenceCount > 0 && (
              <p className="mt-1 max-w-sm text-xs text-muted-foreground">
                Kan inte tas bort: används av {referenceCount} raiddag
                {referenceCount === 1 ? "" : "ar"}
              </p>
            )}
          </div>
        )}
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onValidSubmit)} className="space-y-6">
          {/* Patch identity and the vault thresholds sit in two bordered groups that share one
              row and wrap as the window narrows. */}
          <div className="flex flex-wrap items-start gap-4">
            <fieldset className="shrink-0 space-y-3 rounded-lg border p-4">
              <legend className="px-1 text-sm font-medium">Patch</legend>
              <div className="flex flex-wrap gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="w-40">
                      <FormLabel>Namn</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="startsOn"
                  render={({ field }) => (
                    <FormItem className="w-44">
                      <FormLabel>Start</FormLabel>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="endsOn"
                  render={({ field }) => (
                    <FormItem className="w-44">
                      <FormLabel>Slut</FormLabel>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </fieldset>
            <fieldset className="shrink-0 space-y-3 rounded-lg border p-4">
              <legend className="px-1 text-sm font-medium">Vault</legend>
              <div className="flex flex-wrap gap-4">
                <ThresholdField name="vaultThreshold1" label="1 slot" />
                <ThresholdField name="vaultThreshold2" label="2 slots" />
                <ThresholdField name="vaultThreshold3" label="3 slots" />
              </div>
            </fieldset>
          </div>

          <section className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-semibold">Raids</h3>
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  raids.append({
                    id: null,
                    name: "",
                    bosses: [],
                  })
                }
              >
                <Plus />
                Lägg till raid
              </Button>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={onRaidDragStart}
              onDragEnd={onRaidDragEnd}
              onDragCancel={() => setActiveRaidId(null)}
            >
              <div className="grid auto-rows-[1px] grid-flow-row-dense grid-cols-[repeat(auto-fill,minmax(min(19rem,100%),1fr))] gap-x-4">
                {raids.fields.map((raid, raidIndex) => (
                  <MasonryItem key={raid.fieldKey}>
                    <SortableItem
                      id={raidDragId(raid.fieldKey)}
                      label={`Flytta raid ${raidIndex + 1}`}
                    >
                      <PatchRaidEditor
                        raidIndex={raidIndex}
                        raidKey={raid.fieldKey}
                        onRemove={() => raids.remove(raidIndex)}
                      />
                    </SortableItem>
                  </MasonryItem>
                ))}
              </div>
              <DragOverlay dropAnimation={null}>
                {activeRaid && (
                  <RaidDragPreview raid={activeRaid} index={activeRaidIndex} />
                )}
              </DragOverlay>
            </DndContext>

            {form.formState.errors.raids?.message && (
              <p className="text-sm text-destructive">
                {form.formState.errors.raids.message}
              </p>
            )}
          </section>

          <div className="sticky bottom-0 flex justify-end border-t bg-background/95 py-4 backdrop-blur">
            <Button type="submit" loading={isSaving}>
              <Save />
              Spara patch
            </Button>
          </div>
        </form>
      </Form>

      <ReferencedRemovalConfirmation
        removals={pendingConfirmation?.removals ?? []}
        open={pendingConfirmation !== null}
        onOpenChange={(open) => {
          if (!open && !isSaving) setPendingConfirmation(null);
        }}
        onConfirm={() =>
          pendingConfirmation && save(pendingConfirmation.values)
        }
      />

      <PatchDeleteConfirmation
        open={deleteOpen}
        pending={deletePatch.isPending}
        onOpenChange={setDeleteOpen}
        onConfirm={() =>
          baseline && deletePatch.mutate(baseline.id, { onSuccess: onDeleted })
        }
      />
    </section>
  );
}

function ThresholdField({
  name,
  label,
}: {
  name: "vaultThreshold1" | "vaultThreshold2" | "vaultThreshold3";
  label: string;
}) {
  const form = useFormContext<PatchFormInput>();
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="w-28">
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              type="number"
              min={1}
              step={1}
              value={Number.isNaN(field.value) ? "" : field.value}
              onBlur={field.onBlur}
              onChange={(event) =>
                field.onChange(
                  event.target.value === ""
                    ? Number.NaN
                    : event.target.valueAsNumber,
                )
              }
              name={field.name}
              ref={field.ref}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function raidDragId(raidKey: string) {
  return `raid:${raidKey}`;
}

function RaidDragPreview({
  raid,
  index,
}: {
  raid: PatchFormInput["raids"][number];
  index: number;
}) {
  return (
    <section className="h-full w-full space-y-4 rounded-lg border bg-card p-4 shadow-xl">
      <div className="space-y-1.5">
        <p className="text-sm font-medium">Raid</p>
        <div className="min-h-9 rounded-md border bg-background px-3 py-1.5 text-sm">
          {raid.name || `Raid ${index + 1}`}
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-sm font-medium">Bossar</p>
        {raid.bosses.map((boss, bossIndex) => (
          <div
            key={boss.id ?? `boss-${bossIndex}`}
            className="rounded-md border bg-background px-3 py-2 text-sm"
          >
            {boss.name || `Boss ${bossIndex + 1}`}
          </div>
        ))}
      </div>
    </section>
  );
}

const MASONRY_GAP_PX = 16;

function MasonryItem({ children }: { children: ReactNode }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [rowSpan, setRowSpan] = useState(1);

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const updateSpan = () =>
      setRowSpan(
        Math.max(
          1,
          Math.ceil(content.getBoundingClientRect().height + MASONRY_GAP_PX),
        ),
      );

    updateSpan();
    const observer = new ResizeObserver(updateSpan);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  return (
    <div style={{ gridRowEnd: `span ${rowSpan}` }}>
      <div ref={contentRef}>{children}</div>
    </div>
  );
}
